import React, { useState, useEffect } from 'react';

import { Loader, useMantineTheme, MultiSelect, MultiSelectProps, SelectItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteProduct from 'hooks/product/useAutoCompleteProduct';
import { Product } from 'types';
import { getFilteredMultiSelectPrevState } from 'utils/functions';

const ProductMultiSelect = ({
  icon,
  type,
  ...multiSelectProps
}: Omit<MultiSelectProps, 'data'> & {
  onChange: (value: string[]) => void;
  type?: Product['type'] | null;
}): JSX.Element => {
  const theme = useMantineTheme();

  const [autocompleteData, setAutocompleteData] = useState<SelectItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteProduct({
    search: debouncedSearchTerm,
    type: type || undefined,
  });

  useEffect(() => {
    if (isSuccess && data) {
      const resultsFormattedData = data.data.map(productInstance => ({
        value: productInstance.productId,
        label: productInstance.name,
        'data-cy': `prop ${productInstance.name}`,
      }));

      const filteredPrevData = (prevState: SelectItem[]): SelectItem[] =>
        getFilteredMultiSelectPrevState(prevState, resultsFormattedData, multiSelectProps.value || []);

      setAutocompleteData(prevState => [...filteredPrevData(prevState), ...resultsFormattedData]);
    }
  }, [isSuccess, data, multiSelectProps.value]);

  return (
    <MultiSelect
      onSearchChange={setSearchTerm}
      data={autocompleteData}
      nothingFound={isLoading ? 'Products are loading' : 'Products are not found'}
      rightSection={isLoading && <Loader size={theme.fontSizes.md} />}
      searchable
      {...multiSelectProps}
    />
  );
};

export default ProductMultiSelect;
